/**
 * Main entry point for the coding agent CLI.
 *
 * This file handles CLI argument parsing and translates them into
 * createAgentSession() options. The SDK does the heavy lifting.
 */

import { type ImageContent, modelsAreEqual, supportsXhigh } from "@apholdings/jensen-ai";
import chalk from "chalk";
import { createInterface } from "readline";
import { type Args, parseArgs, printHelp } from "./cli/args.js";
import { selectConfig } from "./cli/config-selector.js";
import { processFileArguments } from "./cli/file-processor.js";
import { listModels } from "./cli/list-models.js";
import { parseResumeChildCommand } from "./cli/resume-child-command.js";
import { parseResumeCommand } from "./cli/resume-command.js";
import { selectSession } from "./cli/session-picker.js";
import { APP_NAME, getAgentDir, getModelsPath, VERSION } from "./config.js";
import { handleAssignmentCommand } from "./core/assignment/cli.js";
import { AuthStorage } from "./core/auth-storage.js";
import { handleBenchmarkCommand } from "./core/benchmark/index.js";
import { handleRouteCommand } from "./core/capability-routing/cli.js";
import { checkTodoHealth } from "./core/doctor.js";
import { bindChildSession, defaultChildSessionDir } from "./core/durable-child-session/index.js";
import { handleEvaluationCommand } from "./core/evaluation/cli.js";
import { handleExecutorCommand } from "./core/executor-registry/cli.js";
import { exportFromFile } from "./core/export-html/index.js";
import type { LoadExtensionsResult } from "./core/extensions/index.js";
import { handleGovernanceCommand } from "./core/governance/cli.js";
import { KeybindingsManager } from "./core/keybindings.js";
import { handleAdaptiveCommand } from "./core/long-horizon/adaptive/cli.js";
import { handleMcpCommand } from "./core/mcp-foundation/cli.js";
import { handleMissionCommand } from "./core/mission/cli.js";
import { handleMissionControlCommand } from "./core/mission-control/cli.js";
import { MissionControlService } from "./core/mission-control/index.js";
import { createFileDurableMissionStore } from "./core/mission-durable/index.js";
import { ModelRegistry } from "./core/model-registry.js";
import { resolveCliModel, resolveModelScope, type ScopedModel } from "./core/model-resolver.js";
import { handleOperabilityCommand } from "./core/operability-cli.js";
import { handleOrchestratorCommand } from "./core/orchestration/cli.js";
import { DefaultPackageManager } from "./core/package-manager.js";
import { handleRemoteCommand } from "./core/remote-execution/remote-cli.js";
import { DefaultResourceLoader } from "./core/resource-loader.js";
import { handleRoutingCommand } from "./core/routing/cli.js";
import { handleWorkspaceCommand } from "./core/safety/cli.js";
import { handleSchedulerCommand } from "./core/scheduler/cli.js";
import { type CreateAgentSessionOptions, createAgentSession } from "./core/sdk.js";
import { SessionManager, validateSessionFile } from "./core/session-manager.js";
import { SettingsManager } from "./core/settings-manager.js";
import { handleAgentsCommand, handleInferenceCommand } from "./core/shared-inference/cli.js";
import { handleSubagentCommand } from "./core/subagent-cli.js";
import { printTimings, time } from "./core/timings.js";
import { buildTodoStatusReport, formatTodoStatus, type TodoDiagnosticInput } from "./core/todo/todo-cli.js";
import { allTools } from "./core/tools/index.js";
import { handleUnityCommand } from "./core/unity-mcp/cli.js";
import { handleWorkerCommand } from "./core/worker-daemon/cli.js";
import { handleWorkspaceRetrievalCommand } from "./core/workspace-cli.js";
import { runMigrations, showDeprecationWarnings } from "./migrations.js";
import { InteractiveMode, runPrintMode, runRpcMode } from "./modes/index.js";
import { initTheme, stopThemeWatcher } from "./modes/interactive/theme/theme.js";

/**
 * Read all content from piped stdin.
 * Returns undefined if stdin is a TTY (interactive terminal).
 */
async function readPipedStdin(): Promise<string | undefined> {
	// If stdin is a TTY, we're running interactively - don't read stdin
	if (process.stdin.isTTY) {
		return undefined;
	}

	return new Promise((resolve) => {
		let data = "";
		process.stdin.setEncoding("utf8");
		process.stdin.on("data", (chunk) => {
			data += chunk;
		});
		process.stdin.on("end", () => {
			resolve(data.trim() || undefined);
		});
		process.stdin.resume();
	});
}

function reportSettingsErrors(settingsManager: SettingsManager, context: string): void {
	const errors = settingsManager.drainErrors();
	for (const { scope, error } of errors) {
		console.error(chalk.yellow(`Warning (${context}, ${scope} settings): ${error.message}`));
		if (error.stack) {
			console.error(chalk.dim(error.stack));
		}
	}
}

function isTruthyEnvFlag(value: string | undefined): boolean {
	if (!value) return false;
	return value === "1" || value.toLowerCase() === "true" || value.toLowerCase() === "yes";
}

type PackageCommand = "install" | "remove" | "update" | "list";

interface PackageCommandOptions {
	command: PackageCommand;
	source?: string;
	local: boolean;
	help: boolean;
	invalidOption?: string;
}

function getPackageCommandUsage(command: PackageCommand): string {
	switch (command) {
		case "install":
			return `${APP_NAME} install <source> [-l]`;
		case "remove":
			return `${APP_NAME} remove <source> [-l]`;
		case "update":
			return `${APP_NAME} update [source]`;
		case "list":
			return `${APP_NAME} list`;
	}
}

function printPackageCommandHelp(command: PackageCommand): void {
	switch (command) {
		case "install":
			console.log(`${chalk.bold("Usage:")}
  ${getPackageCommandUsage("install")}

Install a package and add it to settings.

Options:
  -l, --local    Install project-locally (.pi/settings.json)

Examples:
  ${APP_NAME} install npm:@foo/bar
  ${APP_NAME} install git:github.com/user/repo
  ${APP_NAME} install git:git@github.com:user/repo
  ${APP_NAME} install https://github.com/user/repo
  ${APP_NAME} install ssh://git@github.com/user/repo
  ${APP_NAME} install ./local/path
`);
			return;

		case "remove":
			console.log(`${chalk.bold("Usage:")}
  ${getPackageCommandUsage("remove")}

Remove a package and its source from settings.

Options:
  -l, --local    Remove from project settings (.pi/settings.json)

Example:
  ${APP_NAME} remove npm:@foo/bar
`);
			return;

		case "update":
			console.log(`${chalk.bold("Usage:")}
  ${getPackageCommandUsage("update")}

Update installed packages.
If <source> is provided, only that package is updated.
`);
			return;

		case "list":
			console.log(`${chalk.bold("Usage:")}
  ${getPackageCommandUsage("list")}

List installed packages from user and project settings.
`);
			return;
	}
}

function parsePackageCommand(args: string[]): PackageCommandOptions | undefined {
	const [command, ...rest] = args;
	if (command !== "install" && command !== "remove" && command !== "update" && command !== "list") {
		return undefined;
	}

	let local = false;
	let help = false;
	let invalidOption: string | undefined;
	let source: string | undefined;

	for (const arg of rest) {
		if (arg === "-h" || arg === "--help") {
			help = true;
			continue;
		}

		if (arg === "-l" || arg === "--local") {
			if (command === "install" || command === "remove") {
				local = true;
			} else {
				invalidOption = invalidOption ?? arg;
			}
			continue;
		}

		if (arg.startsWith("-")) {
			invalidOption = invalidOption ?? arg;
			continue;
		}

		if (!source) {
			source = arg;
		}
	}

	return { command, source, local, help, invalidOption };
}

async function handlePackageCommand(args: string[]): Promise<boolean> {
	const options = parsePackageCommand(args);
	if (!options) {
		return false;
	}

	if (options.help) {
		printPackageCommandHelp(options.command);
		return true;
	}

	if (options.invalidOption) {
		console.error(chalk.red(`Unknown option ${options.invalidOption} for "${options.command}".`));
		console.error(chalk.dim(`Use "${APP_NAME} --help" or "${getPackageCommandUsage(options.command)}".`));
		process.exitCode = 1;
		return true;
	}

	const source = options.source;
	if ((options.command === "install" || options.command === "remove") && !source) {
		console.error(chalk.red(`Missing ${options.command} source.`));
		console.error(chalk.dim(`Usage: ${getPackageCommandUsage(options.command)}`));
		process.exitCode = 1;
		return true;
	}

	const cwd = process.cwd();
	const agentDir = getAgentDir();
	const settingsManager = SettingsManager.create(cwd, agentDir);
	reportSettingsErrors(settingsManager, "package command");
	const packageManager = new DefaultPackageManager({ cwd, agentDir, settingsManager });

	packageManager.setProgressCallback((event) => {
		if (event.type === "start") {
			process.stdout.write(chalk.dim(`${event.message}\n`));
		}
	});

	try {
		switch (options.command) {
			case "install":
				await packageManager.install(source!, { local: options.local });
				packageManager.addSourceToSettings(source!, { local: options.local });
				console.log(chalk.green(`Installed ${source}`));
				return true;

			case "remove": {
				await packageManager.remove(source!, { local: options.local });
				const removed = packageManager.removeSourceFromSettings(source!, { local: options.local });
				if (!removed) {
					console.error(chalk.red(`No matching package found for ${source}`));
					process.exitCode = 1;
					return true;
				}
				console.log(chalk.green(`Removed ${source}`));
				return true;
			}

			case "list": {
				const globalSettings = settingsManager.getGlobalSettings();
				const projectSettings = settingsManager.getProjectSettings();
				const globalPackages = globalSettings.packages ?? [];
				const projectPackages = projectSettings.packages ?? [];

				if (globalPackages.length === 0 && projectPackages.length === 0) {
					console.log(chalk.dim("No packages installed."));
					return true;
				}

				const formatPackage = (pkg: (typeof globalPackages)[number], scope: "user" | "project") => {
					const source = typeof pkg === "string" ? pkg : pkg.source;
					const filtered = typeof pkg === "object";
					const display = filtered ? `${source} (filtered)` : source;
					console.log(`  ${display}`);
					const path = packageManager.getInstalledPath(source, scope);
					if (path) {
						console.log(chalk.dim(`    ${path}`));
					}
				};

				if (globalPackages.length > 0) {
					console.log(chalk.bold("User packages:"));
					for (const pkg of globalPackages) {
						formatPackage(pkg, "user");
					}
				}

				if (projectPackages.length > 0) {
					if (globalPackages.length > 0) console.log();
					console.log(chalk.bold("Project packages:"));
					for (const pkg of projectPackages) {
						formatPackage(pkg, "project");
					}
				}

				return true;
			}

			case "update":
				await packageManager.update(source);
				if (source) {
					console.log(chalk.green(`Updated ${source}`));
				} else {
					console.log(chalk.green("Updated packages"));
				}
				return true;
		}
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : "Unknown package command error";
		console.error(chalk.red(`Error: ${message}`));
		process.exitCode = 1;
		return true;
	}
}

async function prepareInitialMessage(
	parsed: Args,
	autoResizeImages: boolean,
): Promise<{
	initialMessage?: string;
	initialImages?: ImageContent[];
}> {
	if (parsed.fileArgs.length === 0) {
		return {};
	}

	const { text, images } = await processFileArguments(parsed.fileArgs, { autoResizeImages });

	let initialMessage: string;
	if (parsed.messages.length > 0) {
		initialMessage = text + parsed.messages[0];
		parsed.messages.shift();
	} else {
		initialMessage = text;
	}

	return {
		initialMessage,
		initialImages: images.length > 0 ? images : undefined,
	};
}

/** Result from resolving a session argument */
type ResolvedSession =
	| { type: "path"; path: string } // Direct file path
	| { type: "local"; path: string } // Found in current project
	| { type: "global"; path: string; cwd: string } // Found in different project
	| { type: "not_found"; arg: string }; // Not found anywhere

/**
 * Resolve a session argument to a file path.
 * If it looks like a path, use as-is. Otherwise try to match as session ID prefix.
 */
async function resolveSessionPath(sessionArg: string, cwd: string, sessionDir?: string): Promise<ResolvedSession> {
	// If it looks like a file path, use as-is
	if (sessionArg.includes("/") || sessionArg.includes("\\") || sessionArg.endsWith(".jsonl")) {
		return { type: "path", path: sessionArg };
	}

	// Try to match as session ID in current project first
	const localSessions = await SessionManager.list(cwd, sessionDir);
	const localMatches = localSessions.filter((s) => s.id.startsWith(sessionArg));

	if (localMatches.length >= 1) {
		return { type: "local", path: localMatches[0].path };
	}

	// Try global search across all projects
	const allSessions = await SessionManager.listAll();
	const globalMatches = allSessions.filter((s) => s.id.startsWith(sessionArg));

	if (globalMatches.length >= 1) {
		const match = globalMatches[0];
		return { type: "global", path: match.path, cwd: match.cwd };
	}

	// Not found anywhere
	return { type: "not_found", arg: sessionArg };
}

function printResumeHelp(): void {
	console.log(`${chalk.bold("Usage:")}
  ${APP_NAME} resume <SESSION_ID>

Continue a previously persisted session identified by its exact session ID.
The requested session is restored with its conversation history, model/thinking
configuration, and working directory. New activity continues under the same
session ID.

Example:
  ${APP_NAME} resume 9ad37e10-243e-4fe6-9d7c-59c2e327aabc

Session IDs are shown by /session and in the --resume session picker.
`);
}

function printResumeChildHelp(): void {
	console.log(`${chalk.bold("Usage:")}
  ${APP_NAME} resume-child <MISSION_ID>

Explicitly resume an INTERRUPTED durable child mission. The child restores the
SAME durable AgentSession and the SAME logical mission, but allocates a NEW
execution attempt, then continues remaining work from its operational checkpoint.

Example:
  ${APP_NAME} resume-child mission_1b2f...
`);
}

/**
 * Handle the explicit `resume-child <MISSION_ID>` command. Loads the durable
 * mission, resolves + validates the bound child session, and drives the
 * coordinator to a canonical terminal result with a new attempt/execution.
 */
async function handleResumeChildCommand(args: string[]): Promise<boolean> {
	const parsed = parseResumeChildCommand(args);
	if (parsed.kind === "none") return false;

	if (parsed.kind === "help") {
		printResumeChildHelp();
		return true;
	}

	if (parsed.kind === "missing") {
		console.error(chalk.red("Missing required mission ID."));
		console.error(chalk.dim(`\nUsage:\n  ${APP_NAME} resume-child <MISSION_ID>`));
		process.exitCode = 1;
		return true;
	}

	const missionId = parsed.missionId;
	const agentDir = getAgentDir();
	const sessionDir = defaultChildSessionDir(agentDir);
	const store = createFileDurableMissionStore();
	const control = new MissionControlService({ store, sessionDir });

	const cliEntry = process.argv[1];
	const command = process.execPath;
	const prefixArgs = [...process.execArgv, cliEntry];

	try {
		const outcome = await control.resumeMission(missionId, {
			buildResumeLaunch: ({ request, resumePrompt, childSessionId }) => {
				const launchArgs = [
					...prefixArgs,
					"--mode",
					"json",
					"-p",
					"--child-mission",
					missionId,
					"--session-id",
					childSessionId,
					"--session-dir",
					sessionDir,
				];
				if (request.modelPolicy) {
					launchArgs.push("--provider", request.modelPolicy.provider, "--model", request.modelPolicy.model);
				}
				if (request.capabilities && request.capabilities.length > 0) {
					launchArgs.push("--tools", request.capabilities.join(","));
				}
				launchArgs.push(resumePrompt);
				return {
					command,
					args: launchArgs,
					cwd: request.workspaceScope?.cwd ?? process.cwd(),
				};
			},
		});

		console.log(
			JSON.stringify(
				{
					missionId: outcome.missionId,
					parentMissionId: outcome.parentMissionId,
					childSessionId: outcome.childSessionId,
					attemptId: outcome.attemptId,
					executionId: outcome.executionId,
					missionState: outcome.missionState,
					success: outcome.success,
				},
				null,
				2,
			),
		);
	} catch (error) {
		const message = error instanceof Error ? error.message : String(error);
		console.error(chalk.red(message));
		process.exitCode = 1;
	}
	return true;
}

/**
 * Resolve and open a persisted session by exact ID.
 *
 * Fails safely (and never mutates persisted data) when the ID is unknown or the
 * session file is corrupted. A successful open keeps the session identity from
 * the persisted header, so future appends continue under the same session ID.
 */
async function openSessionById(sessionId: string, sessionDir?: string): Promise<SessionManager> {
	const sessionInfo = await SessionManager.findById(sessionId, sessionDir);
	if (!sessionInfo) {
		console.error(chalk.red(`Session not found: ${sessionId}`));
		process.exit(1);
	}

	const validation = validateSessionFile(sessionInfo.path);
	if (!validation.ok) {
		console.error(chalk.red(`Cannot resume session ${sessionId}: ${validation.reason}`));
		console.error(chalk.dim(`Session file: ${sessionInfo.path}`));
		console.error(chalk.dim("The persisted session was left untouched."));
		process.exit(1);
	}

	const sessionManager = SessionManager.open(sessionInfo.path, sessionDir);
	if (sessionManager.getSessionId() !== sessionId) {
		console.error(
			chalk.red(`Session identity mismatch: requested ${sessionId}, loaded ${sessionManager.getSessionId()}`),
		);
		process.exit(1);
	}

	return sessionManager;
}

/** Prompt user for yes/no confirmation */
async function promptConfirm(message: string): Promise<boolean> {
	return new Promise((resolve) => {
		const rl = createInterface({
			input: process.stdin,
			output: process.stdout,
		});
		rl.question(`${message} [y/N] `, (answer) => {
			rl.close();
			resolve(answer.toLowerCase() === "y" || answer.toLowerCase() === "yes");
		});
	});
}

/** Helper to call CLI-only session_directory handlers before the initial session manager is created */
async function callSessionDirectoryHook(extensions: LoadExtensionsResult, cwd: string): Promise<string | undefined> {
	let customSessionDir: string | undefined;

	for (const ext of extensions.extensions) {
		const handlers = ext.handlers.get("session_directory");
		if (!handlers || handlers.length === 0) continue;

		for (const handler of handlers) {
			try {
				const event = { type: "session_directory" as const, cwd };
				const result = (await handler(event)) as { sessionDir?: string } | undefined;

				if (result?.sessionDir) {
					customSessionDir = result.sessionDir;
				}
			} catch (err) {
				const message = err instanceof Error ? err.message : String(err);
				console.error(chalk.red(`Extension "${ext.path}" session_directory handler failed: ${message}`));
			}
		}
	}

	return customSessionDir;
}

async function createSessionManager(
	parsed: Args,
	cwd: string,
	extensions: LoadExtensionsResult,
): Promise<SessionManager | undefined> {
	if (parsed.noSession) {
		return SessionManager.inMemory();
	}

	// CLI flag takes precedence, otherwise ask extensions for custom session directory
	let effectiveSessionDir = parsed.sessionDir;
	if (!effectiveSessionDir) {
		effectiveSessionDir = await callSessionDirectoryHook(extensions, cwd);
	}

	if (parsed.resumeSessionId) {
		return openSessionById(parsed.resumeSessionId, effectiveSessionDir);
	}

	// Explicit stable session identity (durable child execution). Opens the
	// existing session when present (resume) or creates one with the exact id
	// (first execution). Never regenerates the id merely because execution restarts.
	if (parsed.sessionId) {
		const existing = effectiveSessionDir
			? await SessionManager.findByExactIdInDir(parsed.sessionId, effectiveSessionDir)
			: await SessionManager.findById(parsed.sessionId);
		if (existing) {
			const validation = validateSessionFile(existing.path);
			if (!validation.ok) {
				console.error(chalk.red(`Cannot open session ${parsed.sessionId}: ${validation.reason}`));
				process.exit(1);
			}
			const manager = SessionManager.open(existing.path, effectiveSessionDir);
			if (manager.getSessionId() !== parsed.sessionId) {
				console.error(
					chalk.red(`Session identity mismatch: requested ${parsed.sessionId}, loaded ${manager.getSessionId()}`),
				);
				process.exit(1);
			}
			return manager;
		}
		return SessionManager.createWithId(cwd, effectiveSessionDir, parsed.sessionId);
	}

	if (parsed.session) {
		const resolved = await resolveSessionPath(parsed.session, cwd, effectiveSessionDir);

		switch (resolved.type) {
			case "path":
			case "local":
				return SessionManager.open(resolved.path, effectiveSessionDir);

			case "global": {
				// Session found in different project - ask user if they want to fork
				console.log(chalk.yellow(`Session found in different project: ${resolved.cwd}`));
				const shouldFork = await promptConfirm("Fork this session into current directory?");
				if (!shouldFork) {
					console.log(chalk.dim("Aborted."));
					process.exit(0);
				}
				return SessionManager.forkFrom(resolved.path, cwd, effectiveSessionDir);
			}

			case "not_found":
				console.error(chalk.red(`No session found matching '${resolved.arg}'`));
				process.exit(1);
		}
	}
	if (parsed.continue) {
		return SessionManager.continueRecent(cwd, effectiveSessionDir);
	}
	// --resume is handled separately (needs picker UI)
	// If effective session dir is set, create new session there
	if (effectiveSessionDir) {
		return SessionManager.create(cwd, effectiveSessionDir);
	}
	// Default case (new session) returns undefined, SDK will create one
	return undefined;
}

function buildSessionOptions(
	parsed: Args,
	scopedModels: ScopedModel[],
	sessionManager: SessionManager | undefined,
	modelRegistry: ModelRegistry,
	settingsManager: SettingsManager,
): { options: CreateAgentSessionOptions; cliThinkingFromModel: boolean } {
	const options: CreateAgentSessionOptions = {};
	let cliThinkingFromModel = false;

	if (sessionManager) {
		options.sessionManager = sessionManager;
		// Explicit resume continues in the session's original working directory
		// rather than the directory where `jensen resume` was invoked. Empty cwd
		// (legacy sessions) falls back to the current directory via createAgentSession.
		if (parsed.resumeSessionId && sessionManager.getCwd()) {
			options.cwd = sessionManager.getCwd();
		}
	}

	// Model from CLI
	// - supports --provider <name> --model <pattern>
	// - supports --model <provider>/<pattern>
	if (parsed.model) {
		const resolved = resolveCliModel({
			cliProvider: parsed.provider,
			cliModel: parsed.model,
			modelRegistry,
		});
		if (resolved.warning) {
			console.warn(chalk.yellow(`Warning: ${resolved.warning}`));
		}
		if (resolved.error) {
			console.error(chalk.red(resolved.error));
			process.exit(1);
		}
		if (resolved.model) {
			options.model = resolved.model;
			// Allow "--model <pattern>:<thinking>" as a shorthand.
			// Explicit --thinking still takes precedence (applied later).
			if (!parsed.thinking && resolved.thinkingLevel) {
				options.thinkingLevel = resolved.thinkingLevel;
				cliThinkingFromModel = true;
			}
		}
	}

	if (!options.model && scopedModels.length > 0 && !parsed.continue && !parsed.resume && !parsed.resumeSessionId) {
		// Check if saved default is in scoped models - use it if so, otherwise first scoped model
		const savedProvider = settingsManager.getDefaultProvider();
		const savedModelId = settingsManager.getDefaultModel();
		const savedModel = savedProvider && savedModelId ? modelRegistry.find(savedProvider, savedModelId) : undefined;
		const savedInScope = savedModel ? scopedModels.find((sm) => modelsAreEqual(sm.model, savedModel)) : undefined;

		if (savedInScope) {
			options.model = savedInScope.model;
			// Use thinking level from scoped model config if explicitly set
			if (!parsed.thinking && savedInScope.thinkingLevel) {
				options.thinkingLevel = savedInScope.thinkingLevel;
			}
		} else {
			options.model = scopedModels[0].model;
			// Use thinking level from first scoped model if explicitly set
			if (!parsed.thinking && scopedModels[0].thinkingLevel) {
				options.thinkingLevel = scopedModels[0].thinkingLevel;
			}
		}
	}

	// Thinking level from CLI (takes precedence over scoped model thinking levels set above)
	if (parsed.thinking) {
		options.thinkingLevel = parsed.thinking;
	}

	// Scoped models for Ctrl+P cycling
	// Keep thinking level undefined when not explicitly set in the model pattern.
	// Undefined means "inherit current session thinking level" during cycling.
	if (scopedModels.length > 0) {
		options.scopedModels = scopedModels.map((sm) => ({
			model: sm.model,
			thinkingLevel: sm.thinkingLevel,
		}));
	}

	// API key from CLI - set in authStorage
	// (handled by caller before createAgentSession)

	// Tools
	if (parsed.noTools) {
		// --no-tools: start with no built-in tools
		// --tools can still add specific ones back
		if (parsed.tools && parsed.tools.length > 0) {
			options.tools = parsed.tools.map((name) => allTools[name]);
		} else {
			options.tools = [];
		}
	} else if (parsed.tools) {
		options.tools = parsed.tools.map((name) => allTools[name]);
	}

	return { options, cliThinkingFromModel };
}

async function handleConfigCommand(args: string[]): Promise<boolean> {
	if (args[0] !== "config") {
		return false;
	}

	const cwd = process.cwd();
	const agentDir = getAgentDir();
	const settingsManager = SettingsManager.create(cwd, agentDir);
	reportSettingsErrors(settingsManager, "config command");
	const packageManager = new DefaultPackageManager({ cwd, agentDir, settingsManager });

	const resolvedPaths = await packageManager.resolve();

	await selectConfig({
		resolvedPaths,
		settingsManager,
		cwd,
		agentDir,
	});

	process.exit(0);
}

function printJson(payload: unknown): void {
	console.log(JSON.stringify(payload, null, 2));
}

/**
 * `jensen todo ...` — read-only TODO operability diagnostics.
 * Supports: status | inspect | verify. `--json` for machine output.
 * A live engine diagnostic may be injected for session-scoped detail; outside a
 * session it reports the canonical model status.
 */
async function handleTodoCommand(args: string[]): Promise<boolean> {
	if (args[0] !== "todo") {
		return false;
	}
	const sub = args[1] ?? "status";
	const json = args.includes("--json");
	const diagnosticArg = parseTodoDiagnosticFlag(args);

	if (sub === "status" || sub === "inspect") {
		const report = buildTodoStatusReport(diagnosticArg);
		if (json) {
			printJson(report);
		} else {
			console.log(formatTodoStatus(report));
		}
		process.exit(0);
	}

	if (sub === "verify") {
		const check = checkTodoHealth(diagnosticArg);
		if (json) {
			printJson(check);
		} else {
			const icon = check.status === "ok" ? "✓" : check.status === "warn" ? "⚠" : "✗";
			console.log(`${icon} todo: ${check.message}`);
		}
		process.exit(0);
	}

	console.error(chalk.red(`Unknown todo subcommand "${sub}". Supported: status, inspect, verify.`));
	process.exit(1);
}

/**
 * `jensen doctor todo` — read-only TODO subsystem health check.
 */
async function handleDoctorTodoCommand(args: string[]): Promise<boolean> {
	if (args[0] !== "doctor" || args[1] !== "todo") {
		return false;
	}
	const json = args.includes("--json");
	const diagnosticArg = parseTodoDiagnosticFlag(args);
	const check = checkTodoHealth(diagnosticArg);
	if (json) {
		printJson(check);
	} else {
		const icon = check.status === "ok" ? "✓" : check.status === "warn" ? "⚠" : "✗";
		console.log(`${icon} ${check.name}: ${check.message}`);
	}
	process.exit(0);
}

/** Parse an optional `--scope <id>` into a baseline diagnostic for inspection. */
function parseTodoDiagnosticFlag(args: string[]): TodoDiagnosticInput | undefined {
	const idx = args.indexOf("--scope");
	if (idx === -1) {
		return undefined;
	}
	const scopeId = args[idx + 1];
	if (!scopeId) {
		return undefined;
	}
	return { scopeId };
}

export async function main(args: string[]) {
	const offlineMode = args.includes("--offline") || isTruthyEnvFlag(process.env.PI_OFFLINE);
	if (offlineMode) {
		process.env.PI_OFFLINE = "1";
		process.env.PI_SKIP_VERSION_CHECK = "1";
	}

	if (await handlePackageCommand(args)) {
		return;
	}

	if (await handleConfigCommand(args)) {
		return;
	}

	if (await handleBenchmarkCommand(args)) {
		return;
	}

	try {
		if (await handleEvaluationCommand(args)) {
			return;
		}
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : String(error);
		if (process.env.JENSEN_EVAL_JSON !== "1") console.error(chalk.red(`eval error: ${message}`));
		process.exitCode = 4;
		return;
	}

	if (await handleWorkspaceCommand(args)) {
		return;
	}

	if (await handleGovernanceCommand(args)) {
		return;
	}

	if (await handleTodoCommand(args)) {
		return;
	}

	if (await handleDoctorTodoCommand(args)) {
		return;
	}

	if (await handleWorkspaceRetrievalCommand(args)) {
		return;
	}

	if (await handleRoutingCommand(args)) {
		return;
	}

	if (await handleMcpCommand(args)) {
		return;
	}

	if (await handleUnityCommand(args)) {
		return;
	}

	if (await handleOperabilityCommand(args)) {
		return;
	}

	if (await handleOrchestratorCommand(args)) {
		return;
	}

	if (await handleSubagentCommand(args)) {
		return;
	}

	if (await handleAdaptiveCommand(args)) {
		return;
	}

	if (await handleExecutorCommand(args)) {
		return;
	}

	if (await handleAssignmentCommand(args)) {
		return;
	}

	if (await handleRouteCommand(args)) {
		return;
	}

	if (await handleSchedulerCommand(args)) {
		return;
	}

	if (await handleInferenceCommand(args)) {
		return;
	}

	if (await handleAgentsCommand(args)) {
		return;
	}

	if (await handleRemoteCommand(args)) {
		return;
	}

	if (await handleWorkerCommand(args)) {
		return;
	}

	if (await handleMissionControlCommand(args)) {
		return;
	}

	if (await handleMissionCommand(args)) {
		return;
	}

	// Detect the explicit `resume-child <MISSION_ID>` command before generic
	// argument parsing (and before `resume <SESSION_ID>`).
	if (await handleResumeChildCommand(args)) {
		return;
	}

	// Detect the explicit `resume <SESSION_ID>` command before generic argument
	// parsing so the subcommand and its ID are not treated as prompt messages.
	const resumeCommand = parseResumeCommand(args);

	if (resumeCommand.kind === "help") {
		printResumeHelp();
		process.exit(0);
	}

	if (resumeCommand.kind === "missing") {
		console.error(chalk.red("Missing required session ID."));
		console.error(chalk.dim(`\nUsage:\n  ${APP_NAME} resume <SESSION_ID>`));
		process.exit(1);
	}

	const resumeSessionId = resumeCommand.kind === "resume" ? resumeCommand.sessionId : undefined;
	const argsForParse = resumeSessionId !== undefined ? args.slice(2) : args;

	// Run migrations (pass cwd for project-local migrations)
	const { migratedAuthProviders: migratedProviders, deprecationWarnings } = runMigrations(process.cwd());

	// First pass: parse args to get --extension paths
	const firstPass = parseArgs(argsForParse);

	// Early load extensions to discover their CLI flags
	const cwd = process.cwd();
	const agentDir = getAgentDir();
	const settingsManager = SettingsManager.create(cwd, agentDir);
	reportSettingsErrors(settingsManager, "startup");
	const authStorage = AuthStorage.create();
	const modelRegistry = new ModelRegistry(authStorage, getModelsPath());

	const resourceLoader = new DefaultResourceLoader({
		cwd,
		agentDir,
		settingsManager,
		additionalExtensionPaths: firstPass.extensions,
		additionalSkillPaths: firstPass.skills,
		additionalPromptTemplatePaths: firstPass.promptTemplates,
		additionalThemePaths: firstPass.themes,
		noExtensions: firstPass.noExtensions,
		noSkills: firstPass.noSkills,
		noPromptTemplates: firstPass.noPromptTemplates,
		noThemes: firstPass.noThemes,
		systemPrompt: firstPass.systemPrompt,
		appendSystemPrompt: firstPass.appendSystemPrompt,
	});
	await resourceLoader.reload();
	time("resourceLoader.reload");

	const extensionsResult: LoadExtensionsResult = resourceLoader.getExtensions();
	for (const { path, error } of extensionsResult.errors) {
		console.error(chalk.red(`Failed to load extension "${path}": ${error}`));
	}

	// Apply pending provider registrations from extensions immediately
	// so they're available for model resolution before AgentSession is created
	for (const { name, config } of extensionsResult.runtime.pendingProviderRegistrations) {
		modelRegistry.registerProvider(name, config);
	}
	extensionsResult.runtime.pendingProviderRegistrations = [];

	const extensionFlags = new Map<string, { type: "boolean" | "string" }>();
	for (const ext of extensionsResult.extensions) {
		for (const [name, flag] of ext.flags) {
			extensionFlags.set(name, { type: flag.type });
		}
	}

	// Second pass: parse args with extension flags
	const parsed = parseArgs(argsForParse, extensionFlags);

	// Record the explicit resume target for session resolution and model precedence.
	if (resumeSessionId !== undefined) {
		parsed.resumeSessionId = resumeSessionId;
	}

	// Pass flag values to extensions via runtime
	for (const [name, value] of parsed.unknownFlags) {
		extensionsResult.runtime.flagValues.set(name, value);
	}

	if (parsed.version) {
		console.log(VERSION);
		process.exit(0);
	}

	if (parsed.help) {
		printHelp();
		process.exit(0);
	}

	if (parsed.listModels !== undefined) {
		const searchPattern = typeof parsed.listModels === "string" ? parsed.listModels : undefined;
		await listModels(modelRegistry, searchPattern);
		process.exit(0);
	}

	// Read piped stdin content (if any) - skip for RPC mode which uses stdin for JSON-RPC
	if (parsed.mode !== "rpc") {
		const stdinContent = await readPipedStdin();
		if (stdinContent !== undefined) {
			// Force print mode since interactive mode requires a TTY for keyboard input
			parsed.print = true;
			// Prepend stdin content to messages
			parsed.messages.unshift(stdinContent);
		}
	}

	if (parsed.export) {
		let result: string;
		try {
			const outputPath = parsed.messages.length > 0 ? parsed.messages[0] : undefined;
			result = await exportFromFile(parsed.export, outputPath);
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to export session";
			console.error(chalk.red(`Error: ${message}`));
			process.exit(1);
		}
		console.log(`Exported to: ${result}`);
		process.exit(0);
	}

	if (parsed.mode === "rpc" && parsed.fileArgs.length > 0) {
		console.error(chalk.red("Error: @file arguments are not supported in RPC mode"));
		process.exit(1);
	}

	const { initialMessage, initialImages } = await prepareInitialMessage(parsed, settingsManager.getImageAutoResize());
	const isInteractive = !parsed.print && parsed.mode === undefined;
	const mode = parsed.mode || "text";
	initTheme(settingsManager.getTheme(), isInteractive);

	// Show deprecation warnings in interactive mode
	if (isInteractive && deprecationWarnings.length > 0) {
		await showDeprecationWarnings(deprecationWarnings);
	}

	let scopedModels: ScopedModel[] = [];
	const modelPatterns = parsed.models ?? settingsManager.getEnabledModels();
	if (modelPatterns && modelPatterns.length > 0) {
		scopedModels = await resolveModelScope(modelPatterns, modelRegistry);
	}

	// Create session manager based on CLI flags
	let sessionManager = await createSessionManager(parsed, cwd, extensionsResult);

	// Handle --resume: show session picker
	if (parsed.resume) {
		// Initialize keybindings so session picker respects user config
		KeybindingsManager.create();

		// Compute effective session dir for resume (same logic as createSessionManager)
		const effectiveSessionDir = parsed.sessionDir || (await callSessionDirectoryHook(extensionsResult, cwd));

		const selectedPath = await selectSession(
			(onProgress) => SessionManager.list(cwd, effectiveSessionDir, onProgress),
			SessionManager.listAll,
		);
		if (!selectedPath) {
			console.log(chalk.dim("No session selected"));
			stopThemeWatcher();
			process.exit(0);
		}
		sessionManager = SessionManager.open(selectedPath, effectiveSessionDir);
	}

	// Durable child binding: a delegated child carries --child-mission. Bind the
	// session to that mission on first execution and validate the binding on
	// resume. Fails closed on mismatch; never silently rebinds.
	if (parsed.childMission) {
		if (!sessionManager || !sessionManager.isPersisted()) {
			console.error(
				chalk.red("--child-mission requires a persistent session (--no-session is invalid for durable children)"),
			);
			process.exit(1);
		}
		try {
			bindChildSession(sessionManager, parsed.childMission);
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			console.error(chalk.red(`Child session binding failed: ${message}`));
			process.exit(1);
		}
	}

	const { options: sessionOptions, cliThinkingFromModel } = buildSessionOptions(
		parsed,
		scopedModels,
		sessionManager,
		modelRegistry,
		settingsManager,
	);
	sessionOptions.authStorage = authStorage;
	sessionOptions.modelRegistry = modelRegistry;
	sessionOptions.resourceLoader = resourceLoader;

	// Handle CLI --api-key as runtime override (not persisted)
	if (parsed.apiKey) {
		if (!sessionOptions.model) {
			console.error(
				chalk.red("--api-key requires a model to be specified via --model, --provider/--model, or --models"),
			);
			process.exit(1);
		}
		authStorage.setRuntimeApiKey(sessionOptions.model.provider, parsed.apiKey);
	}

	const { session, modelFallbackMessage } = await createAgentSession(sessionOptions);

	if (!isInteractive && !session.model) {
		console.error(chalk.red("No models available."));
		console.error(chalk.yellow("\nSet an API key environment variable:"));
		console.error("  ANTHROPIC_API_KEY, OPENAI_API_KEY, GEMINI_API_KEY, etc.");
		console.error(chalk.yellow(`\nOr create ${getModelsPath()}`));
		process.exit(1);
	}

	// Clamp thinking level to model capabilities for CLI-provided thinking levels.
	// This covers both --thinking <level> and --model <pattern>:<thinking>.
	const cliThinkingOverride = parsed.thinking !== undefined || cliThinkingFromModel;
	if (session.model && cliThinkingOverride) {
		let effectiveThinking = session.thinkingLevel;
		if (!session.model.reasoning) {
			effectiveThinking = "off";
		} else if (effectiveThinking === "xhigh" && !supportsXhigh(session.model)) {
			effectiveThinking = "high";
		}
		if (effectiveThinking !== session.thinkingLevel) {
			session.setThinkingLevel(effectiveThinking);
		}
	}

	if (mode === "rpc") {
		await runRpcMode(session);
	} else if (isInteractive) {
		if (scopedModels.length > 0 && (parsed.verbose || !settingsManager.getQuietStartup())) {
			const modelList = scopedModels
				.map((sm) => {
					const thinkingStr = sm.thinkingLevel ? `:${sm.thinkingLevel}` : "";
					return `${sm.model.id}${thinkingStr}`;
				})
				.join(", ");
			console.log(chalk.dim(`Model scope: ${modelList} ${chalk.gray("(Ctrl+P to cycle)")}`));
		}

		printTimings();
		const mode = new InteractiveMode(session, {
			migratedProviders,
			modelFallbackMessage,
			initialMessage,
			initialImages,
			initialMessages: parsed.messages,
			verbose: parsed.verbose,
		});
		await mode.run();
	} else {
		await runPrintMode(session, {
			mode,
			messages: parsed.messages,
			initialMessage,
			initialImages,
		});
		stopThemeWatcher();
		if (process.stdout.writableLength > 0) {
			await new Promise<void>((resolve) => process.stdout.once("drain", resolve));
		}
		process.exit(0);
	}
}
